import React, { useMemo } from 'react';
import { IoMdClose, IoMdAlert, IoMdTime } from 'react-icons/io';
import './styles.scss';

interface ModalPendenciasProps {
    pontos: any[];
    colaboradores: any[];
    feriados: any[];
    selectedMonth: Date;
    onClose: () => void;
    onResolve: (date: Date, colabId: number) => void;
}

// Helper: Parse HH:mm to minutes
function timeToMinutes(timeStr: string): number {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

const ModalPendencias: React.FC<ModalPendenciasProps> = ({ 
    pontos, 
    colaboradores, 
    feriados, 
    selectedMonth, 
    onClose,
    onResolve
}) => {

    const listaPendencias = useMemo(() => {
        const pendencias: any[] = [];
        const today = new Date();
        const diasNoMes = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();

        colaboradores.forEach(c => {
            // 1. Verificar pontos existentes do colaborador (Tolerância e Status)
            const pontosColab = pontos.filter(p => Number(p.colaboradorId) === Number(c.id));
            
            pontosColab.forEach(p => {
                if (p.status === "PENDENTE") {
                    pendencias.push({
                        id: `p-${p.id || Math.random()}`,
                        tipo: 'Aprovação Pendente',
                        dataStr: p.data,
                        colaborador: c,
                        detalhe: `Solicitação de ajuste: ${p.horario} - ${p.tipo}`,
                        rawDate: p.data, // formato DD/MM/YYYY
                        urgencia: 'alta'
                    });
                } else if (p.status !== "APROVADO" && p.status !== "REPROVADO" && p.status !== "REJEITADO") {
                    // Análise de tolerância ±30 min (ex: 08:00) -> 07:30 a 08:30
                    if (p.tipo && p.tipo.toLowerCase().includes('entrada')) {
                        const m = timeToMinutes(p.horario);
                        if (m < 450) { // Antes das 07:30
                            pendencias.push({
                                id: `t-early-${p.id || Math.random()}`,
                                tipo: 'Entrada Antecipada',
                                dataStr: p.data,
                                colaborador: c,
                                detalhe: `Batida muito cedo: ${p.horario} (Previsto: 08:00)`,
                                rawDate: p.data,
                                urgencia: 'media'
                            });
                        } else if (m > 510) { // Depois das 08:30
                            pendencias.push({
                                id: `t-late-${p.id || Math.random()}`,
                                tipo: 'Atraso',
                                dataStr: p.data,
                                colaborador: c,
                                detalhe: `Entrada após tolerância: ${p.horario} (Previsto: 08:00)`,
                                rawDate: p.data,
                                urgencia: 'media'
                            });
                        }
                    }
                }
            });

            // 2. Verificar Ausências Virtuais (Dias úteis passados sem marcação)
            for (let dia = 1; dia <= diasNoMes; dia++) {
                const checkDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), dia);
                if (checkDate >= today) break; 
                
                if (checkDate.getDay() === 0 || checkDate.getDay() === 6) continue;
                
                const dateStr = checkDate.toISOString().split('T')[0];
                const monthDay = dateStr.substring(5);
                const isFeriado = feriados.some(f => f.data === dateStr || (f.recorrente && f.data && f.data.endsWith(monthDay)));
                if (isFeriado) continue;

                const ptDiaStr = `${String(dia).padStart(2, '0')}/${String(selectedMonth.getMonth() + 1).padStart(2, '0')}/${selectedMonth.getFullYear()}`;
                const hasPonto = pontosColab.some(p => p.data === ptDiaStr);
                
                if (!hasPonto) {
                    pendencias.push({
                        id: `v-${c.id}-${ptDiaStr}`,
                        tipo: 'Ausência (Falta)',
                        dataStr: ptDiaStr,
                        colaborador: c,
                        detalhe: 'Nenhuma marcação registrada no dia útil.',
                        rawDate: ptDiaStr,
                        urgencia: 'alta'
                    });
                }
            }
        });

        // Ordenar: mais urgentes primeiro, depois data mais antiga
        return pendencias.sort((a, b) => {
            if (a.urgencia === 'alta' && b.urgencia !== 'alta') return -1;
            if (b.urgencia === 'alta' && a.urgencia !== 'alta') return 1;
            const da = a.dataStr.split('/').reverse().join('');
            const db = b.dataStr.split('/').reverse().join('');
            return da.localeCompare(db);
        });

    }, [pontos, colaboradores, feriados, selectedMonth]);

    const handleResolveClick = (item: any) => {
        const parts = item.rawDate.split('/');
        const dateObj = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        onResolve(dateObj, Number(item.colaborador.id));
    };

    return (
        <div className="modal-pendencias__overlay" onClick={onClose}>
            <div className="modal-pendencias__container" onClick={e => e.stopPropagation()}>
                <div className="modal-pendencias__header">
                    <div>
                        <h2>Central de Pendências</h2>
                        <p className="modal-pendencias__sub">Resolução de anomalias e aprovações</p>
                    </div>
                    <button className="btn-close" onClick={onClose}><IoMdClose size={22} /></button>
                </div>

                <div className="modal-pendencias__body">
                    {listaPendencias.length === 0 ? (
                        <div className="empty-state">
                            <IoMdAlert className="empty-icon text-green-500" />
                            <p>Nenhuma pendência encontrada! Tudo em ordem.</p>
                        </div>
                    ) : (
                        <div className="pendencias-list">
                            {listaPendencias.map(item => (
                                <div key={item.id} className={`pendencia-item urgencia-${item.urgencia}`}>
                                    <div className="pend-icon">
                                        {item.tipo.includes('Aprovação') ? <IoMdTime /> : <IoMdAlert />}
                                    </div>
                                    <div className="pend-info">
                                        <div className="pend-header">
                                            <span className="colab-name">{item.colaborador.nome}</span>
                                            <span className="pend-date">{item.dataStr}</span>
                                        </div>
                                        <span className="pend-type">{item.tipo}</span>
                                        <p className="pend-detail">{item.detalhe}</p>
                                    </div>
                                    <div className="pend-action">
                                        <button onClick={() => handleResolveClick(item)}>Resolver</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModalPendencias;
